import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { reportsApi, type ManHoursReport as Report, type ManHoursRow } from '@/lib/apiClient';
import { StateView } from '@/components';
import { fmtDate, fmtDateTime, todayInputValue } from '@/lib/dates';

/**
 * Man Hours Report (issue #347) — companion sheet to the Job Cost Management
 * Report. Same sections, same sort, same sign convention; a reader moves
 * between the two without re-learning anything.
 *
 * THE TWO THINGS A READER MUST NOT MISS, both said in plain text on the page:
 *
 *  1. ESTIMATED IS DERIVED — labor dollars ÷ the company rate ($58/MH loaded,
 *     the presidents 2026-08-18, admin-editable in Admin ▸ Config). The rate
 *     used is stated on every render and in the export, so nobody is ever
 *     guessing the divisor. It is NOT the estimator's typed job figure; that
 *     figure is shown alongside as a cross-check.
 *
 *  2. SOME ACTUAL HOURS CAN'T BE PLACED — Paycor books hours against cost-code
 *     NAMES, and ~8.5% of names company-wide don't resolve to a unique code.
 *     Those hours stay visible as their own italicised rows rather than being
 *     dropped or guessed onto a code, so the total always ties to Paycor.
 */

const hrs = (v: number) => v.toLocaleString('en-US', { maximumFractionDigits: 1 });
// The RATE never goes through hrs(): hours are 1dp by design, but the rate must
// read EXACTLY as configured or the stated divisor can't reproduce the figures —
// a configured $58.25/MH shown as "$58.3/MH" is a false provenance, and any
// digit cap just moves the lie further out (review, #352, twice). String(r) is
// the shortest round-trip form of the stored double — for anything an admin
// types ("58.25") it IS the typed text — so group its integer part and keep the
// fraction verbatim. The exponential branch (rates under 1e-6, absurd but
// accepted by validation) shows the e-notation rather than rounding to $0.
const rateFmt = (r: number) => {
  const s = String(r);
  if (s.includes('e') || s.includes('E')) return s;
  const [int, frac] = s.split('.');
  const grouped = Number(int).toLocaleString('en-US');
  return frac ? `${grouped}.${frac}` : grouped;
};
const balanceClass = (v: number) => (v < -0.05 ? 'mhr-over' : v > 0.05 ? 'mhr-under' : '');

const css = `
  .mhr-hd { display:flex; align-items:flex-start; justify-content:space-between; gap:var(--ew-s-4); flex-wrap:wrap; margin-bottom:var(--ew-s-4); }
  .mhr-hd h2 { font:var(--ew-fw-semibold) var(--ew-fs-h3)/var(--ew-lh-snug) var(--ew-font); color:var(--ew-dark); margin:0; }
  .mhr-hd p { margin:4px 0 0; font:var(--ew-fw-regular) var(--ew-fs-body-sm)/1.45 var(--ew-font); color:var(--ew-n-500); }
  .mhr-facts { display:grid; grid-template-columns:repeat(auto-fit,minmax(190px,1fr)); gap:var(--ew-s-3) var(--ew-s-5);
    background:#fff; border:1px solid var(--ew-n-200); border-radius:var(--ew-r-md); padding:var(--ew-s-4) var(--ew-s-5); margin-bottom:var(--ew-s-4); }
  .mhr-fact .l { font:var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing:.06em; text-transform:uppercase; color:var(--ew-n-500); }
  .mhr-fact .v { font:var(--ew-fw-medium) 13.5px/1.3 var(--ew-font); color:var(--ew-dark); margin-top:5px; }
  .mhr-note { border-radius:var(--ew-r-md); padding:9px 12px; font:var(--ew-fw-regular) 12.5px/1.45 var(--ew-font); margin-bottom:var(--ew-s-3); }
  .mhr-note.warn { background:#FBEEE1; color:#8A4E12; }
  .mhr-note.info { background:var(--ew-n-50); color:var(--ew-n-600); }
  .mhr-wrap { background:#fff; border:1px solid var(--ew-n-200); border-radius:var(--ew-r-md); overflow:auto; max-height:70vh; }
  .mhr-table { border-collapse:separate; border-spacing:0; min-width:100%;
    font:var(--ew-fw-regular) 12.5px/1.35 var(--ew-font); font-variant-numeric:tabular-nums; }
  .mhr-table th, .mhr-table td { padding:6px 12px; white-space:nowrap; border-bottom:1px solid #F2F2F1; }
  .mhr-table thead th { position:sticky; top:0; background:var(--ew-n-50); color:var(--ew-n-600); text-align:right;
    font:var(--ew-fw-semibold) 11px/1.2 var(--ew-font); letter-spacing:.04em; text-transform:uppercase; z-index:2;
    border-bottom:1px solid var(--ew-n-200); }
  .mhr-table td.num { text-align:right; }
  .mhr-table td.first, .mhr-table th.first { text-align:left; }
  .mhr-table tbody tr:hover td { background:#FAFAF9; }
  .mhr-sec td { background:var(--ew-n-50); font:var(--ew-fw-semibold) 11px/1.2 var(--ew-font);
    letter-spacing:.06em; text-transform:uppercase; color:var(--ew-green-700); padding-top:10px; padding-bottom:10px; }
  .mhr-tot td { font-weight:600; background:#F7F7F6; border-top:1px solid var(--ew-n-300); border-bottom:1px solid var(--ew-n-300); }
  .mhr-tot.grand td { background:var(--ew-dark); color:#fff; border-color:var(--ew-dark); }
  .mhr-over { color:var(--ew-danger); }
  .mhr-under { color:var(--ew-green-700); }
  .mhr-tot.grand .mhr-over { color:#FF9F91; }
  .mhr-tot.grand .mhr-under { color:#8FE3B0; }
  /* Unmatched Paycor names: real hours, uncertain placement — visibly different
     from a coded row, without shouting. */
  .mhr-unmatched td.first { font-style:italic; color:var(--ew-n-600); }
  .mhr-unmatched .mhr-chip { font:var(--ew-fw-semibold) 10px/1 var(--ew-font); letter-spacing:.05em; text-transform:uppercase;
    background:#FBEEE1; color:#8A4E12; border-radius:3px; padding:3px 6px; margin-left:8px; font-style:normal; }
  .mhr-btn { font:var(--ew-fw-semibold) 12px/1 var(--ew-font); border-radius:var(--ew-r-md); padding:9px 15px; cursor:pointer;
    border:1px solid var(--ew-green); background:var(--ew-green); color:#fff; }
  .mhr-btn:hover { background:var(--ew-green-700); border-color:var(--ew-green-700); }
  .mhr-btn:disabled { opacity:.5; cursor:not-allowed; }
  .mhr-toggle { display:flex; align-items:center; gap:7px; font:var(--ew-fw-medium) 12.5px/1 var(--ew-font); color:var(--ew-n-600); cursor:pointer; }
  .mhr-empty { padding:var(--ew-s-5); text-align:center; color:var(--ew-n-400); font-size:13px; }
`;

export default function ManHoursReport({ projectId }: { projectId: number }) {
  const q = useQuery({
    queryKey: ['reports', projectId, 'man-hours'],
    queryFn: () => reportsApi.manHours(projectId),
  });
  const [showZeroRows, setShowZeroRows] = useState(false);

  return (
    <>
      <style>{css}</style>
      <StateView query={q} isEmpty={() => false} loading="Building the man hours report…">
        {(data) => <Body data={data} showZeroRows={showZeroRows} onToggleZeroRows={setShowZeroRows} />}
      </StateView>
    </>
  );
}

const isZeroRow = (r: ManHoursRow) => r.hours.estimated === 0 && r.hours.actual === 0;

function Body({
  data, showZeroRows, onToggleZeroRows,
}: {
  data: Report;
  showZeroRows: boolean;
  onToggleZeroRows: (v: boolean) => void;
}) {
  const { header, report, provenance } = data;

  const visible = useMemo(() => ({
    costCodes: showZeroRows ? report.costCodes.rows : report.costCodes.rows.filter((r) => !isZeroRow(r)),
    changeOrders: showZeroRows ? report.changeOrders.rows : report.changeOrders.rows.filter((r) => !isZeroRow(r)),
  }), [report, showZeroRows]);

  const hiddenCount =
    (report.costCodes.rows.length - visible.costCodes.length) +
    (report.changeOrders.rows.length - visible.changeOrders.length);

  const hasAnyRow = report.costCodes.rows.length > 0 || report.changeOrders.rows.length > 0;

  return (
    <div>
      <div className="mhr-hd">
        <div>
          <h2>Man Hours Report</h2>
          <p>
            Estimated man hours (labor dollars ÷ ${rateFmt(provenance.rate)}/MH) against actual Paycor hours,
            per cost code. Balance is Estimated − Actual, so a negative balance is over the estimate.
          </p>
        </div>
        <button className="mhr-btn" onClick={() => exportWorkbook(data)} disabled={!hasAnyRow}>
          Export to Excel
        </button>
      </div>

      <div className="mhr-facts">
        <Fact label="Job" value={`${header.jobNumber} — ${header.jobName}`} />
        <Fact label="Customer" value={header.customer} />
        <Fact label="Project Manager" value={header.projectManager} />
        <Fact label="Estimator" value={header.estimator} />
        <Fact label="Rate used" value={`$${rateFmt(provenance.rate)}/MH (loaded)`} />
        {/* The typed job-level figure beside the derived total — the reader's
            cross-check. Prod showed one job where these disagree 30x; putting
            both on screen is what surfaces that as a data problem. */}
        <Fact
          label="Estimator's job total"
          value={provenance.typedJobHours == null ? 'none on file' : `${hrs(provenance.typedJobHours)} hrs (typed)`}
        />
      </div>

      <Provenance provenance={provenance} derivedTotal={report.grandTotal.hours.estimated} />

      {!hasAnyRow ? (
        <div className="mhr-wrap"><p className="mhr-empty">No labor estimate or Paycor hours for this job yet.</p></div>
      ) : (
        <>
          <div className="mhr-wrap">
            <table className="mhr-table">
              <thead>
                <tr>
                  <th className="first">Cost Code</th>
                  <th className="first">Description</th>
                  <th>Estimated MH</th>
                  <th>Actual MH</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                <SectionRows label="Cost Codes" rows={visible.costCodes} totals={report.costCodes.totals} actualsKnown={provenance.actualsAvailable} />
                {report.changeOrders.rows.length > 0 ? (
                  <SectionRows label="Change Orders" rows={visible.changeOrders} totals={report.changeOrders.totals} actualsKnown={provenance.actualsAvailable} />
                ) : null}
                <Row row={report.grandTotal} className="mhr-tot grand" actualsKnown={provenance.actualsAvailable} />
              </tbody>
            </table>
          </div>

          <label className="mhr-toggle" style={{ marginTop: 'var(--ew-s-3)' }}>
            <input type="checkbox" checked={showZeroRows} onChange={(e) => onToggleZeroRows(e.target.checked)} />
            Show cost codes with no estimated and no actual hours
            {!showZeroRows && hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ''}
          </label>
        </>
      )}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="mhr-fact">
      <div className="l">{label}</div>
      <div className="v">{value?.trim() || '—'}</div>
    </div>
  );
}

function SectionRows({
  label, rows, totals, actualsKnown,
}: {
  label: string; rows: ManHoursRow[]; totals: ManHoursRow; actualsKnown: boolean;
}) {
  return (
    <>
      <tr className="mhr-sec"><td className="first" colSpan={5}>{label}</td></tr>
      {rows.map((r) => (
        <Row key={(r.costCode ? `c:${r.costCode}` : `n:${r.description ?? ''}`)} row={r} actualsKnown={actualsKnown} />
      ))}
      <Row row={totals} className="mhr-tot" actualsKnown={actualsKnown} />
    </>
  );
}

function Row({ row, className, actualsKnown }: { row: ManHoursRow; className?: string; actualsKnown: boolean }) {
  const cls = [className, row.unmatchedName ? 'mhr-unmatched' : ''].filter(Boolean).join(' ');
  return (
    <tr className={cls || undefined}>
      <td className="first">
        {row.costCode ?? (row.unmatchedName || className ? '' : '(no cost code)')}
        {row.unmatchedName ? <span className="mhr-chip">unmatched name</span> : null}
      </td>
      <td className="first" title={row.description ?? undefined}>{row.description ?? ''}</td>
      <td className="num">{hrs(row.hours.estimated)}</td>
      {/* ⚠️ A Paycor outage must read as ABSENT, not as zero. The API builds
          these cells from an empty row set when safety-site is unreachable, and
          a rendered 0 with a full remaining balance is a number people copy and
          manage against, warning banner or not (review, #352). Balance goes
          blank with it — est − unknown is unknown. */}
      {actualsKnown ? (
        <>
          <td className="num">{hrs(row.hours.actual)}</td>
          <td className={`num ${balanceClass(row.hours.balance)}`}>{hrs(row.hours.balance)}</td>
        </>
      ) : (
        <>
          <td className="num" title="Paycor hours unavailable">—</td>
          <td className="num" title="Paycor hours unavailable">—</td>
        </>
      )}
    </tr>
  );
}

function Provenance({ provenance: p, derivedTotal }: { provenance: Report['provenance']; derivedTotal: number }) {
  const notes: { kind: 'warn' | 'info'; text: string }[] = [];

  if (p.rateSource === 'default') {
    notes.push({
      kind: 'warn',
      text: `The labor rate setting is missing, so this report used the built-in $${rateFmt(p.rate)}/MH. `
        + 'An admin should set "Estimated Labor $ per Man-Hour" in Admin ▸ Config.',
    });
  }
  if (!p.actualsAvailable) {
    notes.push({
      kind: 'warn',
      text: "Couldn't reach the Paycor hours feed — every Actual figure is missing, not zero.",
    });
  }
  // The incomplete-history trap: the Paycor sync only reaches back to
  // actualsFrom (2025-10-01 on prod). A job that started before that shows
  // real-looking Actuals that silently omit everything worked earlier — job
  // 10519 has $1.35M of actual labor dollars but only 634 synced hours.
  if (p.actualsAvailable && p.actualsFrom && p.jobStartDate && p.jobStartDate < p.actualsFrom) {
    notes.push({
      kind: 'warn',
      text: `This job started ${fmtDate(p.jobStartDate)}, but Paycor hours only go back to ${fmtDate(p.actualsFrom)} — `
        + 'Actual hours are INCOMPLETE and omit everything worked before that date. '
        + 'The balance column understates the overrun accordingly.',
    });
  }
  if (!p.estimateSynced) {
    notes.push({
      kind: 'warn',
      text: 'No estimate lines have synced for this job, so every Estimated figure is 0 — missing data, not a zero estimate.',
    });
  }
  // A switched-off sync with historical rows still present is the sneakier case:
  // estimateSynced stays true, so without this the page presents a FROZEN
  // estimate as live (the Job Cost report already warned; review, #352).
  if (!p.estimateEnabled) {
    notes.push({
      kind: 'warn',
      text: 'The Intacct estimate sync is switched off — Estimated figures reflect the last sync'
        + (p.estimateSyncedAt ? ` (${fmtDateTime(p.estimateSyncedAt)})` : '')
        + ', not the current estimate.',
    });
  }
  // Same disclosure the Job Cost report makes for the dollar figure: pending
  // scope stays IN Estimated so the two reports tie, and the reader is told.
  if (p.pendingChangeHours > 0) {
    notes.push({
      kind: 'info',
      text: `Estimated includes ${hrs(p.pendingChangeHours)} hrs derived from PENDING change scope `
        + '(not yet approved). Left in so this report ties to the Job Cost report.',
    });
  }
  if (p.unmatchedActualHours > 0) {
    notes.push({
      kind: 'info',
      text: `${hrs(p.unmatchedActualHours)} actual hours are booked to Paycor cost-code names that don't match `
        + 'a unique cost code — they appear as their own italicised rows so the totals still tie to Paycor. '
        + 'Fixing a name is a cost-code list edit, not a report problem.',
    });
  }
  if (p.typedJobHours != null && derivedTotal > 0) {
    const ratio = derivedTotal / p.typedJobHours;
    if (ratio > 1.5 || ratio < 0.67) {
      notes.push({
        kind: 'warn',
        text: `The derived total (${hrs(derivedTotal)} hrs) and the estimator's typed job figure `
          + `(${hrs(p.typedJobHours)} hrs) disagree by more than 50% — one of them is likely wrong. `
          + 'Worth checking the typed figure and the labor estimate before managing to either number.',
      });
    }
  }

  return (
    <>
      {notes.map((n, i) => <div key={i} className={`mhr-note ${n.kind}`}>{n.text}</div>)}
      <div className="mhr-note info">
        Estimated hours are DERIVED: each code&apos;s estimated labor dollars ÷ ${rateFmt(p.rate)}/MH
        (the loaded rate the estimators price labor at{p.rateSource === 'config' ? ', set in Admin ▸ Config' : ''}).
        Estimate synced {fmtDateTime(p.estimateSyncedAt)}. Actual hours are Paycor
        timecards from {fmtDate(p.actualsFrom)} through {fmtDate(p.actualsThrough)}.
      </div>
    </>
  );
}

/** Same workbook conventions as the dollars report: numbers as numbers, the
 *  provenance travels with the file, exporter's local date in the filename. */
function exportWorkbook(data: Report) {
  const { header, report, provenance } = data;

  // Same rule as the table: a Paycor outage exports BLANK actual/balance cells,
  // never zeros — a spreadsheet outlives the warning banner it was made under.
  const actualsKnown = provenance.actualsAvailable;
  const rowCells = (r: ManHoursRow) => [
    r.costCode ?? (r.unmatchedName ? `(unmatched name)` : '(no cost code)'),
    r.description ?? '',
    r.hours.estimated,
    actualsKnown ? r.hours.actual : '',
    actualsKnown ? r.hours.balance : '',
  ];

  const aoa: (string | number | null)[][] = [
    [`Man Hours Report — as of ${fmtDateTime(provenance.generatedAt)}`],
    [],
    ['Job', `${header.jobNumber} — ${header.jobName}`],
    ['Customer', header.customer ?? ''],
    ['Project Manager', header.projectManager ?? ''],
    ['Estimator', header.estimator ?? ''],
    ['Rate used', `$${provenance.rate}/MH (loaded; estimated MH = labor $ ÷ rate)`],
    ["Estimator's typed job total", provenance.typedJobHours ?? 'none on file'],
    [],
    ['Cost Code', 'Description', 'Estimated MH', 'Actual MH', 'Balance'],
  ];

  aoa.push(['COST CODES']);
  for (const r of report.costCodes.rows) aoa.push(rowCells(r));
  aoa.push(rowCells(report.costCodes.totals));

  if (report.changeOrders.rows.length) {
    aoa.push([]);
    aoa.push(['CHANGE ORDERS']);
    for (const r of report.changeOrders.rows) aoa.push(rowCells(r));
    aoa.push(rowCells(report.changeOrders.totals));
  }

  aoa.push([]);
  aoa.push(rowCells(report.grandTotal));

  aoa.push([]);
  aoa.push(['Estimated source', `Intacct labor estimate ÷ $${provenance.rate}/MH, synced ${fmtDateTime(provenance.estimateSyncedAt)}`]);
  aoa.push(['Actual source', `Paycor timecards from ${fmtDate(provenance.actualsFrom)} through ${fmtDate(provenance.actualsThrough)}`]);
  if (!provenance.actualsAvailable) aoa.push(['⚠', 'Paycor feed unreachable at export time — Actual is missing, not zero.']);
  if (!provenance.estimateEnabled) aoa.push(['⚠', 'The Intacct estimate sync is switched off — Estimated reflects the last sync, not the current estimate.']);
  if (provenance.actualsAvailable && provenance.actualsFrom && provenance.jobStartDate
      && provenance.jobStartDate < provenance.actualsFrom) {
    aoa.push(['⚠', `Job started ${fmtDate(provenance.jobStartDate)} but Paycor hours only go back to ${fmtDate(provenance.actualsFrom)} — Actual hours are INCOMPLETE.`]);
  }
  if (!provenance.estimateSynced) aoa.push(['⚠', 'No estimate lines have synced — Estimated is missing, not zero.']);
  if (provenance.rateSource === 'default') aoa.push(['⚠', 'Labor rate setting missing — built-in default used.']);
  if (provenance.unmatchedActualHours > 0) {
    aoa.push(['Note', `${provenance.unmatchedActualHours} actual hours sit on unmatched Paycor names (their own rows above).`]);
  }
  if (provenance.pendingChangeHours > 0) {
    aoa.push(['Note', `Estimated includes ${provenance.pendingChangeHours} hrs derived from pending (not-yet-approved) change scope.`]);
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 18 }, { wch: 38 }, { wch: 14 }, { wch: 12 }, { wch: 12 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Man Hours');
  XLSX.writeFile(wb, `man-hours-${header.jobNumber || 'job'}-${todayInputValue()}.xlsx`);
}
